import { IUndoRedoService, UndoGroup, UndoSource, type UndoProvenance, type UndoPushOptions } from "./undo";
import { IUndoRedoElement } from "./action.js";
import { registerSingleton } from "../../instantiation/common/registry.js";
import { SyncDescriptor } from "../../instantiation/common/descriptors";
import { Emitter } from "../../../base/common/events";
import { IContextKeyService } from "../../contextkey/common/contextkey";
import { Disposable, isDisposable } from "../../../base/common/lifecycle";
import { IAnalyticsService } from "../../analytics/common/analytics";

export class UndoRedoService extends Disposable implements IUndoRedoService {

    private readonly _onDidChange = new Emitter<void>();
    public readonly onDidChange = this._onDidChange.event;

    private readonly _onDidDisposeElement = new Emitter<{ element: IUndoRedoElement }>();
    /** see {@link IUndoRedoService.onDidDisposeElement} — the entry PERMANENTLY left
     * history; anything it retained is now truly unreachable */
    public readonly onDidDisposeElement = this._onDidDisposeElement.event;

    /** dispose an element that permanently left history + announce it (group members
     * individually — consumers care about the leaf entries, e.g. a delete inside a
     * grouped gesture) */
    private disposeElement(element: IUndoRedoElement): void {
        if (element instanceof UndoRedoGroup) {
            for (const member of element.members) this.disposeElement(member);
            return;
        }
        if (isDisposable(element)) element.dispose();
        this._onDidDisposeElement.fire({ element });
    }

    constructor(
        @IContextKeyService private readonly contextKeyService: IContextKeyService,
        @IAnalyticsService private readonly analytics: IAnalyticsService,
    ) {
        super();

        const canUndo = this.contextKeyService.createKey<boolean>("undoRedo.hasUndo", false);
        const canRedo = this.contextKeyService.createKey<boolean>("undoRedo.hasRedo", false);

        this._register(this.onDidChange(e => {
            canUndo.set(this.canUndo);
            canRedo.set(this.canRedo);
        }));
    }

    dispose(): void {
        super.dispose();
        for (const action of this.undoStack) {
            this.disposeElement(action);
        }
        for (const action of this.redoStack) {
            this.disposeElement(action);
        }
    }


    readonly undoStack: IUndoRedoElement[] = [];
    readonly redoStack: IUndoRedoElement[] = [];

    maxLength: number = 100;

    private _isRedoing: boolean = false;
    private _isUndoing: boolean = false;


    private readonly explicitGroups = new Map<number, { token: UndoGroup; actions: IUndoRedoElement[]; source?: UndoSource }>();

    beginGroup(label: string, code = "unknown"): UndoGroup {
        this.resolveGroup();
        const token = new UndoGroup(label, code);
        this.explicitGroups.set(token.id, { token, actions: [] });
        return token;
    }

    endGroup(group: UndoGroup): void {
        const pending = this.explicitGroups.get(group.id);
        if (!pending || pending.token !== group) throw new Error("Undo group is not active.");
        this.explicitGroups.delete(group.id);
        if (pending.actions.length === 0) return;
        // A concurrent ungrouped edit may still be in the time-based buffer.
        // Land it first; the explicit transaction completed after it.
        this.resolveGroup();
        const source = pending.source;
        const provenance = source?.provenance ?? commonProvenance(pending.actions);
        this.internalPush(new UndoRedoGroup(group.label, group.code, pending.actions, source?.id,
            provenance, Date.now()));
    }

    createSource(provenance: UndoProvenance): UndoSource {
        return new UndoSource(provenance);
    }

    push(action: IUndoRedoElement, options: UndoPushOptions = {}) {
        if (this._isUndoing) {
            return; // Prevent re-entrancy
        }

        if (options.group) {
            const pending = this.explicitGroups.get(options.group.id);
            if (!pending || pending.token !== options.group) throw new Error("Cannot push to an inactive undo group.");
            if (pending.source && options.source && pending.source !== options.source) {
                throw new Error("One undo group cannot contain actions from different sources.");
            }
            pending.source ??= options.source;
            pending.actions.push(stampAction(action, options.source));
            return;
        }

        action = stampAction(action, options.source);

        // grouping OFF (tests/scripted sequences): every push is its own entry,
        // immediately on the stack — no timer, no coalescing
        if (this.groupingDelay <= 0) {
            this.pushGroup = [action];
            this.resolveGroup();
            return;
        }

        // Automatically group actions within a short time frame
        if (!this.pushGroup) {
            this.pushGroup = [];
            setTimeout(() => this.resolveGroup(), this.groupingDelay);
        }
        this.pushGroup.push(action);
    }


    get canUndo(): boolean {
        // entries still sitting in the grouping buffer count — an undo will flush
        // them first, so they ARE undoable right now
        return this.undoStack.length > 0 || (this.pushGroup?.length ?? 0) > 0;
    }

    /** undo/redo operations run STRICTLY one after another: an ASYNC action
     * (a placement redo re-loads its file) must settle before the next pop, or
     * a rapid Ctrl+Z/Y chain interleaves — the create-cube redo ran while the
     * placement was still loading and re-attached into the dead instance
     * (task 80). Failures are contained per operation; the chain never jams. */
    private _pending: Promise<void> = Promise.resolve();

    private enqueue(operation: () => Promise<void>): Promise<void> {
        const run = this._pending.then(operation);
        this._pending = run.catch(() => { /* reported by the operation itself */ });
        return run;
    }

    undo(): Promise<void> {
        return this.enqueue(async () => {
            // Flush the grouping buffer BEFORE popping: pushes land on the real
            // stack only after a 100ms grouping window, so an undo issued within
            // that window (fast typing, AI/scripted edits) would otherwise pop an
            // OLDER action and silently undo the wrong thing.
            this.resolveGroup();
            const action = this.undoStack.pop();
            if (action) {
                this.analytics?.logEvent("undoRedo.undo", { action: action.label, code: action.code });
                // commands the undo triggers must not push NEW entries (same
                // re-entrancy rule as before; push() checks the flag)
                this._isUndoing = true;
                try {
                    await action.undo();
                }
                finally {
                    this._isUndoing = false;
                }
                this.redoStack.push(action);
                this._onDidChange.fire();
            }
        });
    }

    get canRedo() {
        return this.redoStack.length > 0;
    }

    redo(): Promise<void> {
        return this.enqueue(async () => {
            // A pending push is a NEW action — flushing it clears the redo stack,
            // which is the correct outcome (a new edit invalidates redo). Without
            // this, redo would replay a stale future on top of the pending edit.
            this.resolveGroup();
            const action = this.redoStack.pop();
            if (action) {
                this.analytics?.logEvent("undoRedo.redo", { action: action.label, code: action.code });
                this._isRedoing = true;
                try {
                    await action.perform();
                }
                finally {
                    this._isRedoing = false;
                }
                this.undoStack.push(action);
                this._onDidChange.fire();
            }
        });
    }

    clear() {
        for (const action of this.undoStack) {
            this.disposeElement(action);
        }
        for (const action of this.redoStack) {
            this.disposeElement(action);
        }
        this.undoStack.length = 0;
        this.redoStack.length = 0;
        this._onDidChange.fire();
    }



    /**
     * Changes to the undo stack within a certain amount of time are automatically grouped together.
     */
    private pushGroup: IUndoRedoElement[] | null = null;
    /** see {@link IUndoRedoService.groupingDelay} — 0 disables grouping */
    groupingDelay: number = 100; // milliseconds

    private resolveGroup() {
        if (!this.pushGroup) {
            return;
        }

        const runs: IUndoRedoElement[][] = [];
        for (const action of this.pushGroup) {
            const current = runs[runs.length - 1];
            if (!current || !sameSource(current[0], action)) runs.push([action]);
            else current.push(action);
        }
        this.pushGroup = null;
        for (const items of runs) {
            if (items.length === 1) this.internalPush(items[0]);
            else this.internalPush(new UndoRedoGroup(
                items[0].label,
                items.find(a => a.code)?.code || 'unknown',
                items,
                items[0].sourceId,
                commonProvenance(items),
                items[0].timestamp,
            ));
        }
    }

    private internalPush(action: IUndoRedoElement) {
        this.undoStack.push(action);
        // Clear redo stack on new action — those entries are gone for good, so
        // dispose them (releases objects retained for undo, e.g. deleted nodes)
        for (const dropped of this.redoStack) {
            this.disposeElement(dropped);
        }
        this.redoStack.length = 0;
        if (this.undoStack.length > this.maxLength) {
            const evicted = this.undoStack.shift(); // Remove oldest action
            if (evicted) this.disposeElement(evicted);
        }
        this._onDidChange.fire();
    }
}
registerSingleton(IUndoRedoService, new SyncDescriptor(UndoRedoService));



class UndoRedoGroup extends Disposable implements IUndoRedoElement {
    constructor(
        public readonly label: string,
        public readonly code: string,
        private readonly actions: IUndoRedoElement[],
        public readonly sourceId?: number,
        public readonly provenance?: UndoProvenance,
        public readonly timestamp?: number,
    ) {
        super();
    }

    async undo(): Promise<void> {
        for (let i = this.actions.length - 1; i >= 0; i--) {
            await this.actions[i].undo();
        }
    }
    async perform(): Promise<void> {
        for (const action of this.actions) {
            await action.perform();
        }
    }
    /** members exposed so the service can dispose + announce leaf entries individually */
    get members(): readonly IUndoRedoElement[] {
        return this.actions;
    }

    override dispose(): void {
        super.dispose();
        // NOTE: leaf disposal happens via the service's disposeElement (which announces
        // each member); this direct dispose only runs if a group is disposed OUTSIDE the
        // service, where no announcement is possible
        for (const action of this.actions) {
            action.dispose?.();
        }
    }
}

function stampAction(action: IUndoRedoElement, source?: UndoSource): IUndoRedoElement {
    if (!source || (action.sourceId === source.id && action.provenance === source.provenance)) return action;
    return {
        label: action.label,
        code: action.code,
        data: action.data,
        sourceId: source.id,
        provenance: source.provenance,
        timestamp: action.timestamp ?? Date.now(),
        undo: () => action.undo(),
        perform: () => action.perform(),
        dispose: action.dispose ? () => action.dispose!() : undefined,
    };
}

function commonProvenance(actions: readonly IUndoRedoElement[]): UndoProvenance | undefined {
    const first = actions[0]?.provenance;
    if (!first) return undefined;
    return actions.every(action => action.provenance?.origin === first.origin
        && action.provenance?.sessionId === first.sessionId
        && action.provenance?.actorId === first.actorId) ? first : undefined;
}

function sameSource(a: IUndoRedoElement, b: IUndoRedoElement): boolean {
    if (a.sourceId !== undefined || b.sourceId !== undefined) return a.sourceId === b.sourceId;
    return a.provenance?.origin === b.provenance?.origin
        && a.provenance?.sessionId === b.provenance?.sessionId
        && a.provenance?.actorId === b.provenance?.actorId;
}
